/**
 * ChainSelector.jsx
 *
 * Standalone component for viewing and removing PDB chains before submission.
 * Displays all chains parsed from the loaded PDB text, lets the user toggle
 * them on/off, and surfaces a confirmation banner when chains are deselected
 * (so the user knows the filtered structure — not the original — will be sent).
 *
 * Props:
 *   availableChains  {string[]}   All chains detected in the current PDB
 *   selectedChains   {string[]}   Currently-kept chains (controlled by parent)
 *   onChange         {fn}         Called with next selected string[] on any toggle
 *   disabled         {boolean}    Greys out the control (e.g. while loading)
 */

import React from 'react';
import { Info, TriangleAlert } from 'lucide-react';

// ─── styles (module-scoped object — no external dependency) ──────────────────
const s = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#444',
    letterSpacing: '0.01em',
    textTransform: 'uppercase',
  },
  selectAll: {
    fontSize: 11,
    color: '#7c5cbf',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontWeight: 500,
  },
  chainRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  // Chain pill — active/inactive variants applied inline
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 20,
    border: '1.5px solid',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    letterSpacing: '0.05em',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.12s ease',
  },
  pillActive: {
    background: '#ede9f8',
    borderColor: '#b9a9e4',
    color: '#5a3fa0',
  },
  pillInactive: {
    background: '#f5f5f5',
    borderColor: '#ddd',
    color: '#aaa',
    textDecoration: 'line-through',
  },
  pillDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  // Applied to the last remaining selected chain — can't remove it
  pillLocked: {
    cursor: 'not-allowed',
    opacity: 0.6,
    boxShadow: 'none',
  },
  emptyMsg: {
    fontSize: 12,
    color: '#bbb',
    fontStyle: 'italic',
  },
  // Warning banner shown when some chains are removed
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    background: '#fffbeb',
    border: '1px solid #f5d87a',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 11,
    color: '#7a5a00',
    lineHeight: 1.5,
  },
  // Info banner shown when all chains selected (default state)
  infoBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    background: '#f5f0ff',
    border: '1px solid #d9cff5',
    borderRadius: 8,
    padding: '7px 10px',
    fontSize: 11,
    color: '#5a3fa0',
    lineHeight: 1.5,
  },
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function ChainSelector({
  availableChains = [],
  selectedChains = [],
  onChange,
  disabled = false,
}) {
  if (availableChains.length === 0) {
    return (
      <div style={s.root}>
        <div style={s.header}>
          <span style={s.label}>Chains</span>
        </div>
        <span style={s.emptyMsg}>Load a structure to see available chains.</span>
      </div>
    );
  }

  const allSelected   = selectedChains.length === availableChains.length;
  const someRemoved   = selectedChains.length < availableChains.length;
  const removedChains = availableChains.filter((c) => !selectedChains.includes(c));
  const isOnlyChain   = availableChains.length === 1;

  function toggleChain(chain) {
    if (disabled) return;
    // Can't remove the last remaining selected chain
    if (selectedChains.includes(chain) && selectedChains.length === 1) return;
    const next = selectedChains.includes(chain)
      ? selectedChains.filter((c) => c !== chain)
      : [...selectedChains, chain].sort();
    onChange?.(next);
  }

  function handleSelectAll() {
    if (disabled) return;
    onChange?.([...availableChains]);
  }

  return (
    <div style={s.root}>
      <div style={s.header}>
        <span style={s.label}>Chains</span>
        {!allSelected && (
          <button style={s.selectAll} onClick={handleSelectAll}>
            Restore all
          </button>
        )}
      </div>

      <div style={s.chainRow}>
        {availableChains.map((chain) => {
          const isActive = selectedChains.includes(chain);
          return (
            <span
              key={chain}
              style={{
                ...s.pill,
                ...(isActive ? s.pillActive : s.pillInactive),
                ...(disabled ? s.pillDisabled : {}),
              }}
              onClick={() => toggleChain(chain)}
              title={isActive ? `Remove chain ${chain}` : `Restore chain ${chain}`}
            >
              {chain}
            </span>
          );
        })}
      </div>

      {someRemoved && (
        <div style={s.warningBanner}>
          <TriangleAlert
            size={13}
            color="#b07d00"
            strokeWidth={2}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <span>
            Chain{removedChains.length > 1 ? 's' : ''}{' '}
            <strong>{removedChains.join(', ')}</strong>{' '}
            {removedChains.length > 1 ? 'are' : 'is'} excluded. The API will
            receive a filtered PDB containing only chain
            {selectedChains.length > 1 ? 's' : ''}{' '}
            <strong>{selectedChains.join(', ')}</strong>.
          </span>
        </div>
      )}

      {allSelected && availableChains.length > 0 && (
        <div style={s.infoBanner}>
          <Info
            size={13}
            color="#7c5cbf"
            strokeWidth={2}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <span>
            All {availableChains.length} chain
            {availableChains.length > 1 ? 's' : ''} selected. Click a chain pill
            to exclude it from the job.
          </span>
        </div>
      )}
    </div>
  );
}